import { AppThunkAction, AppThunkActionAsync } from "..";
import * as actions from "./actions";
import clientApi from "../../api/client";
import { IClientListFilter, IClientListState, selectClientList } from ".";
import { AxiosResponse, CancelToken } from "axios";
import {
  listEffectCountFilter,
  listEffectGetFilter,
  listEffectLoad,
  listEffectReload,
  listEffectsCountCheck,
  listEffectsGetCheck,
  listEffectsGetCount,
  listEffectsGetNew,
  listEffectsGetParams,
} from "../listEffect";
import { ClientStateType, IClientItem } from "../../models/client";
import { CLIENT_LIST_PAGE_STORAGE_KEY } from "../../utils/consts";
import { setStoredListPage } from "../../utils/listPageStorage";
import { getAvailableInformationTypes } from "../informationType";

export function getClientList(cancelToken: CancelToken): AppThunkActionAsync {
  return async (dispatch, getState) => {
    const state = selectClientList(getState());
    if (listEffectsGetCheck(state.state)) {
      return;
    }

    if (
      (state.filter && state.filter.clientId) ||
      (state.filter?.clientFirstName &&
        state.filter?.clientLastName &&
        state.filter?.clientIdentityCode &&
        state.filter?.clientCity &&
        state.filter?.clientStreet)
    ) {
      const params = new URLSearchParams();
      listEffectGetFilter(params, state.filter);
      addClientParams(params, state);

      let newState = listEffectsGetNew(state);
      dispatch(actions.clientListFetchRequest(newState.newCountState));

      if (state.filter.clientId) {
        params.append("ClientId", state.filter.clientId.toString());
        params.append("SearchById", "true");
      }
      if (
        state.filter?.clientFirstName &&
        state.filter?.clientLastName &&
        state.filter?.clientIdentityCode &&
        state.filter?.clientCity &&
        state.filter?.clientStreet
      ) {
        params.append("FirstName", state.filter.clientFirstName.toString());
        params.append("LastName", state.filter.clientLastName.toString());
        params.append(
          "IdentityCode",
          state.filter.clientIdentityCode.toString()
        );
        params.append("City", state.filter.clientCity.toString());
        params.append("Street", state.filter.clientStreet.toString());
        params.append("SearchById", "false");
      }

      const client = await clientApi.getClientFromList({
        params,
        cancelToken,
      });

      const clientWithDataAsArray: AxiosResponse<IClientItem[]> = {
        ...client,
        data: [client.data],
      };

      cancelToken.throwIfRequested();
      newState = listEffectsGetCount(newState, clientWithDataAsArray);

      dispatch(
        actions.clientListFetchSuccess({
          data: clientWithDataAsArray.data,
          newCountState: newState.newCountState,
          newCount: newState.newCount,
        })
      );

      return;
    }

    let newState = listEffectsGetNew(state);
    dispatch(actions.clientListFetchRequest(newState.newCountState));
    try {
      const params = listEffectsGetParams(state, newState.take);
      listEffectGetFilter(params, state.filter);
      addClientParams(params, state);

      const clients = await clientApi.getList({ params, cancelToken });
      cancelToken.throwIfRequested();
      newState = listEffectsGetCount(newState, clients);

      dispatch(
        actions.clientListFetchSuccess({
          data: clients.data,
          newCountState: newState.newCountState,
          newCount: newState.newCount,
        })
      );
    } catch {
      dispatch(actions.clientListFetchError(cancelToken.reason !== undefined));
    }
  };
}

export function getClientListCount(
  cancelToken: CancelToken
): AppThunkActionAsync {
  return async (dispatch, getState) => {
    const state = selectClientList(getState());
    if (listEffectsCountCheck(state.countState)) {
      return;
    }

    dispatch(actions.clientListCountRequest());
    try {
      const params = listEffectCountFilter(state.filter);
      addClientParams(params, state);

      const count = await clientApi.getCount({ params, cancelToken });
      cancelToken.throwIfRequested();

      dispatch(actions.clientListCountSuccess(count.data));
    } catch {
      dispatch(actions.clientListCountError(cancelToken.reason !== undefined));
    }
  };
}

export function changeClientListOrder(
  orderBy: string,
  orderDesc: boolean
): AppThunkAction {
  return async (dispatch) => {
    setStoredListPage(CLIENT_LIST_PAGE_STORAGE_KEY, 0);
    dispatch(actions.clientListChangeOrder({ orderBy, orderDesc }));
  };
}

export function changeClientListPage(page: number): AppThunkAction {
  return async (dispatch) => {
    setStoredListPage(CLIENT_LIST_PAGE_STORAGE_KEY, page);
    dispatch(actions.clientListChangePage(page));
  };
}

export function changeClientListFilter(
  filter: IClientListFilter
): AppThunkAction {
  return async (dispatch) => {
    setStoredListPage(CLIENT_LIST_PAGE_STORAGE_KEY, 0);
    dispatch(actions.clientListChangeFilter(filter));
  };
}

export function openClientListFilter(): AppThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch(
      actions.clientListChangeFilter({
        ...state.clientList.filter!,
        isOpen: true,
      })
    );
  };
}

export function clientListLoad(reload: boolean): AppThunkAction {
  return async (dispatch, getState) => {
    dispatch(getAvailableInformationTypes());
    const state = selectClientList(getState());
    reload = listEffectReload(reload, state);
    if (reload) {
      dispatch(actions.clientListReset());
    } else {
      const newState = listEffectLoad(state);
      dispatch(
        actions.clientListLoadAction({
          newState: newState.newState,
          newCountState: newState.newCountState,
        })
      );
    }
  };
}

export function addClientParams(
  params: URLSearchParams,
  state: IClientListState
) {
  if (state.filter!.isStateBasic) {
    params.append("States", ClientStateType.Basic);
  }
  if (state.filter!.isStateNew) {
    params.append("States", ClientStateType.New);
  }
  if (state.filter!.isStateActive) {
    params.append("States", ClientStateType.Active);
  }
  if (state.filter!.isStateInActive) {
    params.append("States", ClientStateType.InActive);
  }
  if (state.filter!.isStateRejected) {
    params.append("States", ClientStateType.Rejected);
  }
  if (state.filter!.isStateDead) {
    params.append("States", ClientStateType.Dead);
  }
  if (state.filter!.isStateAnonymised) {
    params.append("States", ClientStateType.Anonymised);
  }

  state.filter!.informationTypeIds.forEach((informationTypeId) => {
    params.append("InformationTypeIds", informationTypeId.toString());
  });

  if (state.filter!.keyCaregiverId) {
    params.append("KeyCaregiverId", state.filter!.keyCaregiverId.toString());
  }
  if (state.filter!.assistanceId) {
    params.append("AssistanceId", state.filter!.assistanceId.toString());
  }
}
